import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Wrench, Cog, Factory, Ship, Droplet, Mountain, UtensilsCrossed, Building2, Target, Eye, ArrowRight, Menu, X, Package, Gauge, Plug, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.scroll-animate').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleWhatsApp = (productName = '', isContactSection = false) => {
    let message = 'Hola, deseo más información sobre sus servicios';
    if (isContactSection) {
      message = 'Hola, deseo información sobre HYDROREPUESTOS.';
    } else if (productName) {
      message = `Hola, deseo cotizar el producto: ${productName}`;
    }
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/593993339371?text=${encodedMessage}`, '_blank');
  };

  const services = [
    {
      image: '/services/ingenieria.jpg',
      title: 'Ingeniería de proyectos',
      description: 'Diseño, construcción, instalación y desarrollo de soluciones personalizadas.'
    },
    {
      image: '/services/cilindros.jpg',
      title: 'Mantenimiento de cilindros hidráulicos y neumáticos',
      description: 'Reparación, diseño y construcción de cilindros hidráulicos con materia prima de la más alta calidad.'
    },
    {
      image: '/services/banco-pruebas.jpg',
      title: 'Banco de pruebas de cilindros hidráulicos',
      description: 'Contamos con un banco de pruebas que verifica el correcto funcionamiento de los cilindros hidráulicos, asegurando calidad, ajustes adecuados y confianza del cliente.'
    },
    {
      image: '/services/electroerosion.jpg',
      title: 'Corte por electroerosión',
      description: 'Servicio de corte por hilo con alta precisión en acero, bronce y aluminio.'
    },
    {
      image: '/services/punzonado.jpg',
      title: 'Punzonado hidráulico',
      description: 'Servicio de punzonado para corte y perforado de láminas metálicas hasta 35 mm de espesor, garantizando rapidez, exactitud y calidad en cada trabajo.'
    }
  ];

  const products = [
    {
      image: '/products/empaques.jpg',
      name: 'Sellos hidráulicos',
      description: 'Contamos con sellos en milímetros y pulgadas para todo tipo de máquinas oleohidráulicas.'
    },
    {
      image: '/products/bombas.jpg',
      name: 'Bombas hidráulicas',
      description: 'Bombas de pistón, paletas y engranajes'
    },
    {
      image: '/products/valvulas.jpg',
      name: 'Válvulas hidráulicas',
      description: 'Válvulas direccionales, de presión y de flujo'
    }
  ];

  const industries = [
    { icon: <Droplet className="w-16 h-16" />, name: 'Oil & Gas' },
    { icon: <Mountain className="w-16 h-16" />, name: 'Minería' },
    { icon: <UtensilsCrossed className="w-16 h-16" />, name: 'Industria Alimenticia' },
    { icon: <Factory className="w-16 h-16" />, name: 'Industrial' },
    { icon: <Ship className="w-16 h-16" />, name: 'Marítima' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md border-b border-gray-200' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                {/* Logo will be added here */}
                <img src="/logo.png" alt="HYDROREPUESTOS" className="h-14 w-auto" onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }} />
                <div className="flex items-center space-x-3" style={{display: 'none'}}>
                  <div className="bg-[#246FC2] p-2 rounded-lg">
                    <Cog className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-[#1F2937]">HYDROREPUESTOS</h1>
                    <p className="text-xs text-[#4B5563]">Componentes Hidráulicos & Mecánicos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('inicio')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Inicio</button>
              <button onClick={() => scrollToSection('nosotros')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Nosotros</button>
              <button onClick={() => scrollToSection('servicios')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Servicios</button>
              <button onClick={() => scrollToSection('productos')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Productos</button>
              <button onClick={() => scrollToSection('industrias')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Industrias</button>
              <button onClick={() => scrollToSection('contacto')} className="text-[#4B5563] hover:text-[#246FC2] transition-colors font-medium">Contacto</button>
              <Button onClick={() => handleWhatsApp()} className="bg-[#246FC2] hover:bg-[#1e5a9d] text-white">
                <Phone className="w-4 h-4 mr-2" />
                Contáctanos
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-[#1F2937]">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden pb-4 space-y-3 border-t border-[#E5E7EB] pt-4">
              <button onClick={() => scrollToSection('inicio')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Inicio</button>
              <button onClick={() => scrollToSection('nosotros')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Nosotros</button>
              <button onClick={() => scrollToSection('servicios')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Servicios</button>
              <button onClick={() => scrollToSection('productos')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Productos</button>
              <button onClick={() => scrollToSection('industrias')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Industrias</button>
              <button onClick={() => scrollToSection('contacto')} className="block w-full text-left text-[#4B5563] hover:text-[#246FC2] py-2">Contacto</button>
              <Button onClick={() => handleWhatsApp()} className="w-full bg-[#246FC2] hover:bg-[#1e5a9d] text-white">
                <Phone className="w-4 h-4 mr-2" />
                Contáctanos
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center bg-[#F5F7FA] overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI0U1RTdFQiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="container mx-auto px-4 lg:px-8 text-center relative z-10 py-32">
          <div className="scroll-animate opacity-0">
            <h1 className="text-5xl md:text-7xl font-bold text-[#1F2937] mb-6 leading-tight">
              Componentes Hidráulicos & Mecánicos
            </h1>
            <p className="text-xl md:text-2xl text-[#4B5563] mb-8 max-w-3xl mx-auto">
              Soluciones oleohidráulicas y electromecánicas con más de 10 años de experiencia
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button onClick={() => handleWhatsApp()} size="lg" className="bg-[#246FC2] hover:bg-[#1e5a9d] text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all">
                <Phone className="w-5 h-5 mr-2" />
                Contáctanos
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => scrollToSection('servicios')} size="lg" variant="outline" className="text-[#246FC2] border-[#246FC2] hover:bg-[#246FC2] hover:text-white text-lg px-8 py-6">
                Ver Servicios
              </Button>
              <Button onClick={() => scrollToSection('productos')} size="lg" variant="outline" className="text-[#246FC2] border-[#246FC2] hover:bg-[#246FC2] hover:text-white text-lg px-8 py-6">
                Ver Productos
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#246FC2] rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#246FC2] rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="scroll-animate opacity-0">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">Quiénes Somos</h2>
                <div className="w-24 h-1 bg-[#246FC2] mx-auto"></div>
              </div>
              
              <div className="bg-[#F5F7FA] rounded-2xl p-8 md:p-12 border border-[#E5E7EB]">
                <p className="text-lg text-[#4B5563] leading-relaxed mb-6">
                  Somos una empresa ecuatoriana con más de <span className="font-bold text-[#246FC2]">10 años de experiencia</span> en el mercado, dedicada a la importación, comercialización, diseño, fabricación y mantenimiento de sistemas oleohidráulicos y electromecánicos para los sectores industrial, alimenticio, minero y petrolero.
                </p>
                <p className="text-lg text-[#4B5563] leading-relaxed">
                  Todos nuestros equipos y sistemas son desarrollados bajo conceptos de <span className="font-bold text-[#246FC2]">alta calidad, garantía, soporte técnico</span>, bajo costo de mantenimiento y transferencia de tecnología, garantizando eficiencia y efectividad para nuestros clientes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-[#F5F7FA]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="scroll-animate opacity-0">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">Nuestros Servicios</h2>
              <div className="w-24 h-1 bg-[#246FC2] mx-auto mb-4"></div>
              <p className="text-xl text-[#4B5563] max-w-2xl mx-auto">
                Soluciones integrales para sus necesidades hidráulicas y electromecánicas
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {services.map((service, index) => (
                <Card key={index} className="scroll-animate opacity-0 bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-[#E5E7EB] group overflow-hidden" style={{animationDelay: `${index * 100}ms`}}>
                  <CardContent className="p-0">
                    <div className="relative h-56 overflow-hidden bg-[#F5F7FA]">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#1F2937] mb-3">{service.title}</h3>
                      <p className="text-[#4B5563]">{service.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="productos" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="scroll-animate opacity-0">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">Productos</h2>
              <div className="w-24 h-1 bg-[#246FC2] mx-auto mb-4"></div>
              <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
                Componentes y repuestos para sistemas oleohidráulicos e industriales
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <Card key={index} className="scroll-animate opacity-0 bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-[#E5E7EB] group overflow-hidden" style={{animationDelay: `${index * 100}ms`}}>
                  <CardContent className="p-0">
                    <div className="relative h-56 overflow-hidden bg-[#F5F7FA]">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#1F2937] mb-3">{product.name}</h3>
                      <p className="text-[#4B5563] mb-6">{product.description}</p>
                      <Button 
                        onClick={() => handleWhatsApp(product.name)} 
                        className="w-full bg-[#246FC2] hover:bg-[#1e5a9d] text-white"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Cotizar por WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industrias" className="py-20 bg-[#F5F7FA]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="scroll-animate opacity-0">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">Industrias que Servimos</h2>
              <div className="w-24 h-1 bg-[#246FC2] mx-auto mb-4"></div>
              <p className="text-xl text-[#4B5563] max-w-2xl mx-auto">
                Experiencia comprobada en múltiples sectores industriales
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {industries.map((industry, index) => (
                <div key={index} className="scroll-animate opacity-0 text-center group" style={{animationDelay: `${index * 100}ms`}}>
                  <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#246FC2] hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
                    <div className="text-[#246FC2] group-hover:scale-110 transition-transform mb-4 flex justify-center">
                      {industry.icon}
                    </div>
                    <p className="font-semibold text-lg text-[#1F2937]">{industry.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="scroll-animate opacity-0">
              <Card className="h-full bg-white border-[#E5E7EB] hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-[#246FC2] p-3 rounded-lg mr-4">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#1F2937]">Misión</h3>
                  </div>
                  <p className="text-lg text-[#4B5563] leading-relaxed">
                    Identificar y brindar soluciones de ingeniería a todas las industrias, mediante la importación, comercialización, diseño, fabricación y mantenimiento de sistemas oleohidráulicos y electromecánicos, respaldados por un servicio técnico confiable y permanente, optimizando recursos y mejorando la satisfacción del cliente.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="scroll-animate opacity-0">
              <Card className="h-full bg-white border-[#E5E7EB] hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-[#246FC2] p-3 rounded-lg mr-4">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#1F2937]">Visión</h3>
                  </div>
                  <p className="text-lg text-[#4B5563] leading-relaxed">
                    Ser reconocidos como una empresa líder en la distribución de partes hidráulicas, diseño, fabricación y reparación de sistemas oleohidráulicos y electromecánicos, desarrollando productos de acuerdo a las necesidades del cliente y convirtiéndonos en un fuerte referente del mercado.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="scroll-animate opacity-0">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">Contáctanos</h2>
              <div className="w-24 h-1 bg-[#246FC2] mx-auto mb-4"></div>
              <p className="text-xl text-[#4B5563]">Estamos listos para ayudarte con tus proyectos</p>
            </div>

            <div className="max-w-7xl mx-auto">
              <Card className="bg-white border-[#E5E7EB] shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Left Column - Contact Information */}
                    <div className="p-8 md:p-12 bg-[#F5F7FA]">
                      <div className="mb-6">
                        <h3 className="text-3xl font-bold text-[#1F2937] mb-2">HYDROREPUESTOS</h3>
                        <p className="text-[#246FC2] font-semibold">Componentes Hidráulicos & Mecánicos</p>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="flex items-start">
                          <MapPin className="w-6 h-6 text-[#246FC2] mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-[#1F2937] mb-1">Dirección</p>
                            <p className="text-[#4B5563]">Calle De los Perales</p>
                            <p className="text-[#4B5563]">Cumbayá, Barrio San Francisco de Pinsha</p>
                            <p className="text-[#4B5563]">Quito, Ecuador</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <Phone className="w-6 h-6 text-[#246FC2] mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-[#1F2937] mb-1">Teléfonos</p>
                            <p className="text-[#4B5563]">0993339371</p>
                            <p className="text-[#4B5563]">0988915317</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <Phone className="w-6 h-6 text-[#246FC2] mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-[#1F2937] mb-1">WhatsApp</p>
                            <p className="text-[#4B5563]">+593 99 333 9371</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <Mail className="w-6 h-6 text-[#246FC2] mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-[#1F2937] mb-1">Correo Electrónico</p>
                            <p className="text-[#4B5563]">conconcede@hotmail.com</p>
                            <p className="text-[#4B5563]">edisonfer_b@hotmail.com</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <Building2 className="w-6 h-6 text-[#246FC2] mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-[#1F2937] mb-1">Contacto Principal</p>
                            <p className="text-[#4B5563]">Ing. Edison Cuñas</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8">
                        <Button 
                          onClick={() => handleWhatsApp('', true)} 
                          size="lg" 
                          className="w-full bg-[#246FC2] hover:bg-[#1e5a9d] text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                        >
                          <Phone className="w-5 h-5 mr-2" />
                          Escríbenos por WhatsApp
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    </div>

                    {/* Right Column - Google Maps */}
                    <div className="relative h-full min-h-[500px] md:min-h-[600px]">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15959.147887847347!2d-78.42726308715821!3d-0.20048989999999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91d59becd9ba22eb%3A0x5e1e8be6e5a8e9e5!2sCalle%20De%20los%20Perales%2C%20Quito%20170184%2C%20Ecuador!5e0!3m2!1ses!2sec!4v1734448800000!5m2!1ses!2sec"
                        width="100%"
                        height="100%"
                        style={{ border: 0, position: 'absolute', top: 0, left: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Ubicación HYDROREPUESTOS"
                        className="rounded-r-lg"
                      />
                      <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg">
                        <p className="text-sm text-[#1F2937] font-semibold">📍 HYDROREPUESTOS</p>
                        <p className="text-xs text-[#4B5563]">Calle De los Perales, Cumbayá, Quito</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-[#4B5563] py-12 border-t border-[#E5E7EB]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src="/logo.png" alt="HYDROREPUESTOS" className="h-12 w-auto" onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }} />
              <div className="flex items-center space-x-3" style={{display: 'none'}}>
                <div className="bg-[#246FC2] p-2 rounded-lg">
                  <Cog className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[#1F2937] font-bold text-lg">HYDROREPUESTOS</p>
                  <p className="text-xs text-[#4B5563]">Componentes Hidráulicos & Mecánicos</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">© 2025 HYDROREPUESTOS. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;